import { I18n } from '@affine/i18n';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import type { ReactElement } from 'react';
import { useEffect, useState, useSyncExternalStore } from 'react';

import * as styles from './ai-operator-presence-overlay.css';
import type {
  AIOperatorPresenceSnapshot,
  AIOperatorPresentationStore,
} from './operator-presentation';

function usePresenceSnapshot(
  store: AIOperatorPresentationStore
): AIOperatorPresenceSnapshot {
  return useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );
}

const scrollRotation = {
  up: 0,
  right: 90,
  down: 180,
  left: 270,
} as const;

function PresenceCursorVisual({
  clicking,
  label,
  x,
  y,
  onStop,
}: {
  clicking: boolean;
  label: string;
  x: number;
  y: number;
  /** Shows a stop button on the name tag while the assistant works. */
  onStop?: () => void;
}) {
  return (
    <div
      className={styles.cursor}
      data-humanized-pointer-cursor="true"
      style={{ left: x, top: y }}
    >
      {clicking ? <span className={styles.clickHalo} /> : null}
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 2L15 8L10 10L8 15L3 2Z"
          fill="currentColor"
          stroke="white"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
      </svg>
      <div className={styles.cursorLabel}>
        {label}
        {onStop ? (
          // Pointer shortcut only; the avatar in the top bar is the keyboard path.
          <button
            type="button"
            tabIndex={-1}
            className={styles.cursorStop}
            title={I18n['com.streamlain.ai.app-use.stop']()}
            data-testid="ai-cursor-stop"
            onClick={onStop}
          >
            <span className={styles.cursorStopGlyph} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function HumanizedPointerOverlay(): ReactElement | null {
  const [configuration] = useState(() => {
    if (typeof window === 'undefined') {
      return { enabled: false, label: BRAND_DISPLAY_NAME };
    }
    const parameters = new URLSearchParams(window.location.search);
    return {
      enabled: parameters.get('humanizedPointer') === '1',
      label:
        parameters.get('humanizedPointerLabel')?.trim() || BRAND_DISPLAY_NAME,
    };
  });
  const [pointer, setPointer] = useState<{
    clicking: boolean;
    visible: boolean;
    x: number;
    y: number;
  }>({ clicking: false, visible: false, x: 0, y: 0 });

  useEffect(() => {
    if (!configuration.enabled) return;
    const move = (event: PointerEvent) =>
      setPointer(current => ({
        ...current,
        visible: true,
        x: event.clientX,
        y: event.clientY,
      }));
    const down = () =>
      setPointer(current => ({ ...current, clicking: true, visible: true }));
    const up = () => setPointer(current => ({ ...current, clicking: false }));
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('pointerup', up, true);
    return () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('pointerup', up, true);
    };
  }, [configuration.enabled]);

  if (!configuration.enabled || !pointer.visible) return null;
  return (
    <div className={styles.viewportOverlay} aria-hidden="true">
      <PresenceCursorVisual
        clicking={pointer.clicking}
        label={configuration.label}
        x={pointer.x}
        y={pointer.y}
      />
    </div>
  );
}

export function AIOperatorPresenceOverlay({
  store,
  coordinateSpace = 'container',
  onStop,
}: {
  store: AIOperatorPresentationStore;
  coordinateSpace?: 'container' | 'viewport';
  onStop?: () => void;
}): ReactElement | null {
  const snapshot = usePresenceSnapshot(store);
  if (!snapshot.visible) return null;

  const assistantLabel = I18n['com.streamlain.ai.bots.personal-assistant']();
  return (
    <div
      className={
        coordinateSpace === 'viewport' ? styles.viewportOverlay : styles.overlay
      }
      data-coordinate-space={coordinateSpace}
      data-ai-cursor
      aria-hidden="true"
      data-testid="ai-operator-presence-overlay"
    >
      {snapshot.fieldRect ? (
        <div
          className={styles.fieldPresence}
          style={{
            left: snapshot.fieldRect.x,
            top: snapshot.fieldRect.y,
            width: snapshot.fieldRect.width,
            height: snapshot.fieldRect.height,
          }}
        >
          <div className={styles.fieldAvatar}>AI</div>
          <div className={styles.fieldLabel}>{assistantLabel}</div>
        </div>
      ) : null}
      {snapshot.cursorVisible && snapshot.cursor ? (
        <PresenceCursorVisual
          clicking={snapshot.clicking}
          label={assistantLabel}
          x={snapshot.cursor.x}
          y={snapshot.cursor.y}
          onStop={onStop}
        />
      ) : null}
      {snapshot.scrollDirection ? (
        <div
          className={styles.scrollBadge}
          style={{
            transform: `rotate(${scrollRotation[snapshot.scrollDirection]}deg)`,
          }}
        >
          <svg
            className={styles.scrollIcon}
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 3L9 15M9 3L4.5 7.5M9 3L13.5 7.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      ) : null}
    </div>
  );
}
